import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useFormValidation } from "../../../hooks/useFormValidation";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import allImages from "../../../assets/images-import";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../Common/FormElements/InputField";
import { ImageInputField } from "../../Common/FormElements/ImageInputField";
import { fetchBannerById, updateBanner } from "../../../services/bannerService";

const UpdateBanner = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const PageLevelAccessurl = "banner/update/:id";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const bannerTitleRef = useRef(null);
  const displayOrderRef = useRef(null);
  const bannerLinkRef = useRef(null);
  const mobileImageInputRef = useRef(null);
  const desktopImageInputRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    Id: id,
    BannerTitle: "",
    BannerLink: "",
    Published: false,
    DisplayOrder: 1000,
    MobileImageFile: null,
    DesktopImageFile: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchBannerById(id);
        if (response && response.isSuccess && response.result) {
          setFormValues({
            Id: response.result.id,
            BannerTitle: response.result.bannerTitle,
            BannerLink: response.result.bannerLink,
            Published: response.result.published,
            DisplayOrder: response.result.displayOrder,
            MobileImageFile: null, // Reset file input
            DesktopImageFile: null, // Reset file input
          });
          // Set mobile banner image URL if available
          if (response.result.mobileImageURL) {
            setMobileImageUrl(response.result.mobileImageURL);
          } else {
            setMobileImageUrl(allImages.ImagePlaceholder);
          }
          // Set desktop image URL if available

          if (response.result.desktopImageURL) {
            setDesktopImageUrl(response.result.desktopImageURL);
          } else {
            setDesktopImageUrl(allImages.ImagePlaceholder);
          }
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchData();
  }, []);

  const [mobileImageUrl, setMobileImageUrl] = useState(
    allImages.ImagePlaceholder
  );
  const [desktopImageUrl, setDesktopImageUrl] = useState(
    allImages.ImagePlaceholder
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // ✅ Register all fields
  useEffect(() => {
    registerField("BannerTitle", bannerTitleRef, "Banner Title");
    registerField("BannerLink", bannerLinkRef, "Banner URL");
    registerField("DisplayOrder", displayOrderRef, "Display Order");
    registerField(
      "MobileImageFile",
      mobileImageInputRef,
      "Mobile Banner Image"
    );
    registerField(
      "DesktopImageFile",
      desktopImageInputRef,
      "Desktop Banner Image"
    );
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  // ✅ Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (mobileImageUrl && mobileImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(mobileImageUrl);
      }
    };
  }, [mobileImageUrl]);

  useEffect(() => {
    return () => {
      if (desktopImageUrl && desktopImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(desktopImageUrl);
      }
    };
  }, [desktopImageUrl]);

  // ✅ Fix input handler for different input types
  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };

      return newValues;
    });
  };

  // ✅ Fix file handling with cleanup
  const handleMobileImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clean up previous URL
      if (mobileImageUrl && mobileImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(mobileImageUrl);
      }

      const newImageUrl = URL.createObjectURL(file);
      setMobileImageUrl(newImageUrl);
      setFormValues((prevState) => ({ ...prevState, MobileImageFile: file }));
    }
  };

  const handleDesktopImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clean up previous URL
      if (desktopImageUrl && desktopImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(desktopImageUrl);
      }

      const newImageUrl = URL.createObjectURL(file);
      setDesktopImageUrl(newImageUrl);
      setFormValues((prevState) => ({ ...prevState, DesktopImageFile: file }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Touch all fields to show validation errors
    touchAllFields();

    // ✅ Validate all fields using refs
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting.");
      return;
    }

    try {
      setIsButtonDisabled(true);

      var responseData = await updateBanner(formValues);

      // ✅ Reset all fields using refs
      // resetAllFields();

      // ✅ Complete form reset
      // setFormValues({
      //   BannerTitle: "",
      //   BannerLink: "",
      //   PageTitle: "",
      //   MetaDescription: "",
      //   Published: false,
      //   DisplayOrder: 1000,
      //   FullDescription: "",
      //   ShortDescription: "",
      //   ImageURLFile: null,
      // });

      // Clean up current image URL
      if (mobileImageUrl && mobileImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(mobileImageUrl);
      }

      if (desktopImageUrl && desktopImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(desktopImageUrl);
      }
      // setImageUrl(allImages.ImagePlaceholder);
      // setContent("");

      // ✅ Clear form errors
      clearErrors();

      if (responseData?.isSuccess) {
        toast.success(responseData?.statusMessage);
      } else {
        toast.error(responseData?.statusMessage);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <ComponentHeader title="Update Banner" />
      {/* ✅ Optional: Show form-level validation status */}
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">Update Banner</h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="Published" className="form-label pe-3">
                    Published Banner
                  </label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="Published"
                      name="Published"
                      checked={formValues.Published}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="Published">
                      {formValues.Published ? "Yes" : "No"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-5 col-md-12 col-sm-12">
                    <InputField
                      ref={bannerTitleRef}
                      label="Banner Title"
                      id="BannerTitle"
                      value={formValues.BannerTitle}
                      placeholder="Enter your Banner Title"
                      onChange={handleInputChange}
                      required={true}
                      validationType="alphanumeric"
                      maxLength={300}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-5 col-md-12 col-sm-12">
                    <InputField
                      ref={bannerLinkRef}
                      label="Banner Link"
                      id="BannerLink"
                      value={formValues.BannerLink}
                      placeholder="Enter Banner Link"
                      onChange={handleInputChange}
                      maxLength={300}
                      required={true}
                      validationType="pathorurl"
                    />
                  </div>

                  <div className="col-lg-2 col-md-12 col-sm-12">
                    <InputField
                      ref={displayOrderRef}
                      label="Display Order"
                      id="DisplayOrder"
                      value={formValues.DisplayOrder}
                      onChange={handleInputChange}
                      placeholder="Enter Display Order"
                      validationType="numberonly"
                      maxLength={5}
                      required={true}
                    />
                  </div>
                </div>

                <div className="text-start">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-12">
            <div className="row">
              <div className="col-lg-6 col-md-6 col-sm-6 col-6">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-sm-1 mt-sm-1">
                      Mobile Banner Image{" "}
                      <span className="required-field">*</span>
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <ImageInputField
                      ref={mobileImageInputRef}
                      id="MobileImageFile"
                      imageUrl={mobileImageUrl}
                      onFileChange={handleMobileImageFileChange}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-6 col-6">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-sm-1 mt-sm-1">
                      Desktop Banner Image{" "}
                      <span className="required-field">*</span>
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <ImageInputField
                      ref={desktopImageInputRef}
                      id="DesktopImageFile"
                      imageUrl={desktopImageUrl}
                      onFileChange={handleDesktopImageFileChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default UpdateBanner;
